// ProfileSessionModal.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaMobileAlt, FaLaptop, FaSignOutAlt } from "react-icons/fa";
import styled from "styled-components";

interface Session {
  session_uid: string;
  created_at: string;
  expiry_datetime: string;
  last_active: string;
  session_timeout: string;
  session_type: string;
  session_status: string;
}

interface DeviceActivity {
  org_uid: string;
  unique_id: string;
  device_name: string;
  device_type: string;
  first_active: string;
  last_active: string;
  manufacturer: string;
  model: string;
  platform: string;
  version_code: number | string;
  sessions: Session[];
  status: string;
  user_uid?: string;
  session_uid?: string;
}

interface ProfileSessionModalProps {
  data: DeviceActivity[];
  onClose: () => void;
  userUid: string;
  sessionUid: string;
  org_uid: string; // added org_uid to fetch sessions
  beAddress?: string;
}

/* styled components (same as before) */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  padding-right: 12px;
`;

const DeviceCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  background: #f9f9f9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const DeviceName = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 4px;
`;

const Info = styled.div`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 2px;
`;

const ExtraInfo = styled.div`
  margin-top: 8px;
  font-size: 0.9rem;
  color: #444;
  background: #eef5ff;
  padding: 12px;
  border-radius: 12px;
`;

const LogoutButton = styled.button`
  background: #d32f2f;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;

  &:hover {
    background: #b71c1c;
  }
`;

const ProfileSessionModal: React.FC<ProfileSessionModalProps> = ({
  data,
  onClose,
  userUid,
  sessionUid,
  org_uid,
  beAddress,
}) => {
  const [loadingLogoutId, setLoadingLogoutId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<DeviceActivity[]>(data);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const baseUrl =
    beAddress ||
    window._env_?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL;

  // --------- fetchUserSession logic ---------
  const fetchUserSession = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/user-device_activity`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        }, withCredentials: true,
        params: {
          org_uid, // Pass org_uid here
        },
      });

      if (
        response.data.status === "Success" &&
        Array.isArray(response.data.data)
      ) {
        setSessions(response.data.data);
        setErrorMessage("");
      } else {
        setErrorMessage(response.data.message || "Unexpected response format");
        setSessions([]);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(
          error.response?.data?.message || "Failed to fetch user sessions"
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unknown error occurred");
      }
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSession();
  }, []);
  // -----------------------------------------

  const handleLogout = async (device: DeviceActivity, session_uid: string) => {
    if (!session_uid) {
      alert("No session id available for this device.");
      return;
    }

    setLoadingLogoutId(session_uid);

    try {
      const payload = {
        org_uid: device.org_uid,
        user_uid: device.user_uid || userUid,
        session_uid,
      };

      await axios.delete(`${baseUrl}/api/user-active-sessions`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        }, withCredentials: true,
        data: payload,
      });

      await fetchUserSession();
    } catch (err) {
      console.error("Logout failed:", err);
      alert("Failed to logout session");
    } finally {
      setLoadingLogoutId(null);
    }
  };

  return (
    <ModalOverlay>
      <ModalBox>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>View Session Details</h3>
          <button
            onClick={onClose}
            style={{
              background: "#e0e0e0",
              border: "none",
              borderRadius: "50%",
              width: 32,
              height: 32,
              cursor: "pointer",
            }}
          >
            ✖
          </button>
        </div>

        <ScrollArea>
          {loading ? (
            <div>Loading sessions...</div>
          ) : errorMessage ? (
            <div style={{ color: "red" }}>{errorMessage}</div>
          ) : sessions.length === 0 ? (
            <div>No sessions</div>
          ) : (
            sessions.map((device, idx) => {
              const Icon =
                device.device_type === "mobile" ? FaMobileAlt : FaLaptop;
              const hasSessions =
                Array.isArray(device.sessions) && device.sessions.length > 0;

              return (
                <DeviceCard key={idx}>
                  <Icon size={28} color="#176164" />
                  <div style={{ flex: 1 }}>
                    <DeviceName>
                      {device.device_name || "(Unnamed Device)"}
                    </DeviceName>
                    <Info>Platform: {device.platform || "-"}</Info>
                    <Info>Model: {device.model || "-"}</Info>
                    <Info>
                      Last Active:{" "}
                      {device.last_active
                        ? new Date(device.last_active).toLocaleString()
                        : "Never logged in"}
                    </Info>

                    {hasSessions &&
                      device.sessions.map((session) => (
                        <ExtraInfo key={session.session_uid}>
                          <div>Session UID: {session.session_uid}</div>
                          <div>
                            Created At:{" "}
                            {new Date(session.created_at).toLocaleString()}
                          </div>
                          <div>
                            Last Active:{" "}
                            {new Date(session.last_active).toLocaleString()}
                          </div>
                          <div>
                            Expiry:{" "}
                            {new Date(session.expiry_datetime).toLocaleString()}
                          </div>

                          <LogoutButton
                            onClick={() =>
                              handleLogout(device, session.session_uid)
                            }
                            disabled={loadingLogoutId === session.session_uid}
                          >
                            <FaSignOutAlt />
                            {loadingLogoutId === session.session_uid
                              ? "Logging out..."
                              : "Logout"}
                          </LogoutButton>
                        </ExtraInfo>
                      ))}
                  </div>
                </DeviceCard>
              );
            })
          )}
        </ScrollArea>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ProfileSessionModal;
